import { FormIntakeSubmission, FormIntakeSchemaDef } from '../../services/api';

const esc = (v: unknown): string =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const fmt = (s?: string) => (s ? new Date(s).toLocaleString() : '—');

const PRINT_CSS = `
  @page { margin: 18mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, 'Segoe UI', Roboto, Arial, sans-serif; color: #111; font-size: 12px; line-height: 1.45; margin: 0; }
  h1 { font-size: 20px; margin: 0 0 6px; }
  .meta { border-bottom: 2px solid #111; padding-bottom: 8px; margin-bottom: 14px; color: #333; }
  .meta div { margin: 1px 0; }
  .meta b { display: inline-block; min-width: 92px; }
  h2 { font-size: 14px; margin: 16px 0 6px; padding-bottom: 3px; border-bottom: 1px solid #bbb; break-after: avoid; }
  .item { margin: 0 0 8px; padding-bottom: 6px; border-bottom: 1px dotted #ccc; break-inside: avoid; }
  .item:last-child { border-bottom: 0; }
  .row { margin: 1px 0; }
  .label { color: #555; margin-right: 6px; }
  .val { white-space: pre-wrap; }
`;

/**
 * Print one submission, mirroring the Submissions tab detail view (same sections, same
 * empty-answer skipping), headed by the form's name. Rendered into a hidden iframe so the
 * app's own theme/CSS never leaks into the printout and no popup blocker can interfere.
 * Every dynamic value is HTML-escaped — answers are visitor-supplied.
 */
export const printSubmission = (
  formName: string,
  schema: FormIntakeSchemaDef | undefined,
  sub: FormIntakeSubmission,
  who: string | null,
): void => {
  const data = sub.data || {};
  const sectionsHtml = (schema?.sections || [])
    .map((sec) => {
      const secData = data[sec.key];
      if (secData === undefined || secData === null) return '';
      const items: any[] = sec.repeating ? (Array.isArray(secData) ? secData : []) : [secData];
      if (items.length === 0) return '';
      const itemsHtml = items
        .map((item) => {
          const rows = (sec.fields || [])
            .map((f) => {
              const val = item?.[f.id];
              if (val === undefined || val === null || val === '') return '';
              const text = Array.isArray(val) ? val.join(', ') : String(val);
              return `<div class="row"><span class="label">${esc(f.label || f.id)}:</span><span class="val">${esc(text)}</span></div>`;
            })
            .join('');
          return rows ? `<div class="item">${rows}</div>` : '';
        })
        .join('');
      return itemsHtml ? `<h2>${esc(sec.title || sec.key)}</h2>${itemsHtml}` : '';
    })
    .join('');

  const meta: [string, string][] = [
    ['Submitted by', who || '—'],
    ['Source', sub.source || '—'],
    ['Submitted', fmt(sub.submitted_at)],
    ['CRM synced', sub.crm_synced_at ? fmt(sub.crm_synced_at) : '—'],
  ];
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(formName)}</title>
<style>${PRINT_CSS}</style></head><body>
<h1>${esc(formName)}</h1>
<div class="meta">${meta.map(([k, v]) => `<div><b>${esc(k)}</b>${esc(v)}</div>`).join('')}</div>
${sectionsHtml || '<p>No answers recorded.</p>'}
</body></html>`;

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(iframe);
  const win = iframe.contentWindow;
  const doc = win?.document;
  if (!win || !doc) {
    document.body.removeChild(iframe);
    return;
  }
  doc.open();
  doc.write(html);
  doc.close();
  const cleanup = () => {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  };
  win.addEventListener('afterprint', cleanup);
  // Let layout settle before the print dialog; fallback cleanup if afterprint never fires.
  setTimeout(() => {
    win.focus();
    win.print();
    setTimeout(cleanup, 60000);
  }, 100);
};
